import { WrenchScrewdriverIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

import { PaintMatchPill, PartRequestSourcePill } from "./PartRequestPills";
import { canAccessFeature } from "../../auth/featureScopes";
import { useAuth } from "../../contexts/AuthContext";
import usePartRequestLeadAlerts from "../../data/usePartRequestLeadAlerts";
import { PartRequestMatchStrength } from "../../models/partRequests/IPartRequest";
import { partRequestVehicleLabel } from "../../utils/partRequests";

/**
 * Shown on a lead when its car may have a part someone has asked for. Hidden when nothing matches
 * (including when the customer doesn't have Part Requests - the API returns an empty list).
 */
export default function PartRequestLeadAlert({ leadId }: { leadId: string }) {
  const navigate = useNavigate();
  const { featureScopes } = useAuth();
  const { data } = usePartRequestLeadAlerts(leadId);

  // Lead handlers without parts access still see the alert, but can't open the request page.
  const canOpenRequests = canAccessFeature(featureScopes, "parts");
  const alerts = data?.data ?? [];
  if (alerts.length === 0) return null;

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 shadow">
      <div className="flex items-start gap-3">
        <WrenchScrewdriverIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900">
            {alerts.length === 1 ? "1 open part request" : `${alerts.length} open part requests`} may want parts from this car
          </p>
          <ul className="mt-2 divide-y divide-amber-200">
            {alerts.map((alert) => (
              <li key={alert.partRequestId} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                <div className="min-w-0">
                  <p>
                    <span className="font-medium text-gray-900">{alert.partWanted}</span>
                    <span className="ml-2 text-gray-600">{partRequestVehicleLabel(alert)}</span>
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-700">
                    <PartRequestSourcePill source={alert.source} />
                    {alert.strength === PartRequestMatchStrength.Exact && <span className="font-semibold text-green-800">Same registration</span>}
                    {alert.paintMatch && <PaintMatchPill />}
                    {alert.partNumber && <span>Part no. <span className="font-mono">{alert.partNumber}</span></span>}
                    {alert.paintCode && !alert.paintMatch && <span>Paint <span className="font-mono">{alert.paintCode}</span></span>}
                  </div>
                </div>
                {canOpenRequests && (
                  <button
                    type="button"
                    onClick={() => navigate(`/part-requests/${alert.partRequestId}`)}
                    className="whitespace-nowrap font-medium text-primary-600 hover:underline"
                  >
                    View request
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
